import type { ReplyStats } from '../../api';

/**
 * The few date and number strings the project page and the settings screens
 * need: a row's relative time, a usage window's reset line, and a token count.
 * Pure, with `now` passed in, so they are tested rather than eyeballed at three
 * different times of day — the same reasoning as composer/usageText.ts, kept
 * separate because these screens are a different builder's files and the two
 * outputs read differently (a chat row's age against a calendar day, not a
 * countdown).
 */

const DAY = 24 * 60 * 60;

function startOfDay(seconds: number): number {
  const d = new Date(seconds * 1000);
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

/**
 * A chat or project row's timestamp, the prototype's forms: "Now", "5 min",
 * "Yesterday", a weekday for the rest of the week, then "12 Sep".
 */
export function relativeTime(at: number, now: number = Date.now() / 1000): string {
  const diff = Math.max(0, now - at);
  const daysAgo = Math.max(0, Math.floor((startOfDay(now) - startOfDay(at)) / DAY));

  if (daysAgo === 0) {
    if (diff < 60) return 'Now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    return `${Math.floor(diff / 3600)} hr`;
  }
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo <= 6) return new Date(at * 1000).toLocaleDateString('en-GB', { weekday: 'short' });
  return new Date(at * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * The Usage tab's reset line: "Resets in 3 hr 3 min" inside a day, "Resets Wed
 * 05:00" beyond — empty when nothing has been used, so there is nothing to
 * reset (`resetsAt` is null exactly then, per wire.ts's `UsageWindow`).
 */
export function resetText(resetsAt: number | null, now: number = Date.now() / 1000): string {
  if (resetsAt === null) return '';
  const left = Math.max(0, resetsAt - now);

  if (left < DAY) {
    const totalMin = Math.max(1, Math.ceil(left / 60));
    const hours = Math.floor(totalMin / 60);
    const minutes = totalMin % 60;
    const when = !hours ? `${minutes} min` : minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
    return `Resets in ${when}`;
  }

  const at = new Date(resetsAt * 1000);
  const day = at.toLocaleDateString('en-GB', { weekday: 'short' });
  const time = `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
  return `Resets ${day} ${time}`;
}

/** A token count with a thin space between groups of three, e.g. "12 345". */
export function formatTokens(n: number): string {
  const digits = Math.max(0, Math.round(n)).toString();
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** Units (1/100,000 US dollar) as money: "$1.23", "$0.0021", "$0". */
export function dollars(units: number): string {
  if (units <= 0) return '$0';
  const usd = units / 100_000;
  return `$${usd >= 0.1 ? usd.toFixed(2) : usd.toPrecision(2)}`;
}

/** A reply's figures on one line, e.g. "gpt-oss · 1,234 in · 56 out · $0.0021 · 4.2 s · 2 rounds".
 *  An image made by command has no tokens and no rounds, so says neither. */
export function statsLine(s: ReplyStats, model: string): string {
  return [
    model,
    ...(s.tokensIn || s.tokensOut ? [`${s.tokensIn.toLocaleString('en-GB')} in`, `${s.tokensOut.toLocaleString('en-GB')} out`] : []),
    s.billing === 'mauvely' ? dollars(s.units) : 'own key',
    `${(s.ms / 1000).toFixed(1)} s`,
    ...(s.rounds ? [`${s.rounds} ${s.rounds === 1 ? 'round' : 'rounds'}`] : [])
  ].join(' · ');
}
